import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faBoxOpen,
  faBullhorn,
  faHourglassHalf,
  faEye,
  faEyeSlash,
  faUpload,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import './PricesAndOffers.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = (extra = {}) => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}`, ...extra } });

/* tab key -> table name the server expects (same as before) */
const TABS = [
  { key: 'packages', table: 'PackagesPrice', label: 'Packages', one: 'package', icon: faBoxOpen },
  { key: 'sectionOffers', table: 'OfferSection', label: 'Section offers', one: 'section offer', icon: faBullhorn },
  { key: 'seasonOffers', table: 'OfferSeason', label: 'Season offers', one: 'season offer', icon: faHourglassHalf },
];

// same starting values as before
const EMPTY = {
  PackageName: '',
  Price: 0,
  OfferPrice: 0,
  SessionPrice: 0,
  NbOfSessions: 0,
  NumberOfLevels: 0,
  SessionsPerWeek: 0,
  StyleButton: '',
  Title: '',
  Details: '',
  Img: null,
  Approved: false,
  SalePercent: '',
  CountdownTimeDate: '',
  Style: '',
};

const formatForDateTimeInput = (dateString) => {
  if (!dateString) return '';
  try {
    return format(parseISO(dateString), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};
const parse = (s) => {
  if (!s) return null;
  try {
    const d = parseISO(s);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const num = (v) => Number(v) || 0;
const money = (v) => `$${num(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const isOn = (v) => v === true || v === 1 || v === '1' || v === 'true';

/* reads the colour out of a CSS string like "color: rgb(245,152,49);" or "background-color: #a2cdee;" */
const colourOf = (style) => {
  const m = String(style || '').match(/(?:background-color|background|color)\s*:\s*([^;]+)/i);
  return m ? m[1].trim() : '';
};

const countdown = (d) => {
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return { text: 'Ended', ended: true };
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  return { text: days > 0 ? `${days}d ${hours}h left` : `${hours}h left`, ended: false };
};

const PricesAndOffers = () => {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === params.get('tab')) || TABS[0];

  const [data, setData] = useState({ PackagesPrice: [], OfferSection: [], OfferSeason: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);

  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [toDelete, setToDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState('');

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'packages') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  // ---------- Load (same endpoint) ----------
  const fetchData = useCallback(async () => {
    const response = await axios.get(`${API}/api/admin/prices/prices`, auth());
    const d = response.data || {};
    setData({ PackagesPrice: d.PackagesPrice || [], OfferSection: d.OfferSection || [], OfferSeason: d.OfferSeason || [] });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchData()
      .catch((err) => {
        if (!cancelled) setLoadError(err.response?.data?.error || 'We couldn’t load prices and offers.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchData, reload]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Escape closes; no page scroll behind a window
  const anyOpen = Boolean(showModal || toDelete);
  useEffect(() => {
    if (!anyOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (toDelete) {
        if (!deleteBusy) setToDelete(null);
      } else if (!saving) setShowModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [anyOpen, toDelete, saving, deleteBusy]);

  // ---------- Form (same as before) ----------
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData(EMPTY);
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item, CountdownTimeDate: item.CountdownTimeDate ? formatForDateTimeInput(item.CountdownTimeDate) : '' });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  // same endpoints + same FormData (every field that isn't null/undefined, plus "image")
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) fd.append(key, value);
      });
      if (imageFile) fd.append('image', imageFile);
      const opts = auth({ 'Content-Type': 'multipart/form-data' });
      if (editingItem) await axios.put(`${API}/api/admin/prices/${editingItem.Id}?table=${tab.table}`, fd, opts);
      else await axios.post(`${API}/api/admin/prices/add/${tab.table}`, fd, opts);
      await fetchData();
      setShowModal(false);
      setToast(`${tab.one[0].toUpperCase()}${tab.one.slice(1)} ${editingItem ? 'saved' : 'added'}`);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Couldn’t save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete (same endpoint) ----------
  const handleDelete = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await axios.delete(`${API}/api/admin/prices/${toDelete.Id}?table=${tab.table}`, auth());
      await fetchData();
      setToDelete(null);
      setToast('Deleted');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Couldn’t delete. Please try again.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const askDelete = (item) => {
    setDeleteError('');
    setToDelete(item);
  };

  const nameOf = (item) => item.PackageName || item.Title || 'this item';

  // ---------- Cards ----------
  const renderActions = (item) => (
    <div className="rmapo-actions">
      <button type="button" onClick={() => handleEdit(item)}>
        <FontAwesomeIcon icon={faPen} /> Edit
      </button>
      <button type="button" className="is-danger" onClick={() => askDelete(item)} aria-label={`Delete ${nameOf(item)}`}>
        <FontAwesomeIcon icon={faTrashCan} />
      </button>
    </div>
  );

  const renderLive = (on) => (
    <span className={`rmapo-pill ${on ? 'is-live' : 'is-hidden'}`}>
      <FontAwesomeIcon icon={on ? faEye : faEyeSlash} /> {on ? 'Live' : 'Hidden'}
    </span>
  );

  const renderPackages = () =>
    data.PackagesPrice.map((p) => {
      const offer = num(p.OfferPrice) > 0 && num(p.OfferPrice) < num(p.Price);
      const save = offer ? Math.round((1 - num(p.OfferPrice) / num(p.Price)) * 100) : 0;
      const colour = colourOf(p.Style);
      return (
        <li key={p.Id} className="rmapo-card" style={colour ? { '--rmapo-accent': colour } : undefined}>
          <div className="rmapo-card-top">
            <h3>{p.PackageName}</h3>
            {offer && <span className="rmapo-pill is-save">Save {save}%</span>}
          </div>
          <div className="rmapo-price">
            <strong>{money(offer ? p.OfferPrice : p.Price)}</strong>
            {offer && <s>{money(p.Price)}</s>}
          </div>
          <ul className="rmapo-facts">
            <li>
              <b>{num(p.NbOfSessions)}</b> sessions
            </li>
            <li>
              <b>{money(p.SessionPrice)}</b> per session
            </li>
            <li>
              <b>{num(p.SessionsPerWeek)}</b> / week
            </li>
            <li>
              <b>{num(p.NumberOfLevels)}</b> {num(p.NumberOfLevels) === 1 ? 'level' : 'levels'}
            </li>
          </ul>
          {(p.Style || p.StyleButton) && (
            <p className="rmapo-code">
              {colour && <i className="rmapo-swatch" style={{ background: colour }} />}
              {p.StyleButton && <code>{p.StyleButton}</code>}
            </p>
          )}
          {renderActions(p)}
        </li>
      );
    });

  const renderSectionOffers = () =>
    data.OfferSection.map((o) => {
      const colour = colourOf(o.Style);
      return (
        <li key={o.Id} className="rmapo-card rmapo-card--offer" style={colour ? { '--rmapo-banner': colour } : undefined}>
          <div className="rmapo-banner">
            <FontAwesomeIcon icon={faBullhorn} />
            {renderLive(isOn(o.Approved))}
          </div>
          <h3>{o.Title}</h3>
          {/* Details can hold HTML for the website; shown here as plain text so nothing runs in the dashboard */}
          {o.Details && <p className="rmapo-text">{String(o.Details).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}</p>}
          <p className="rmapo-file">
            <FontAwesomeIcon icon={faImage} /> {o.Img || 'No image'}
          </p>
          {renderActions(o)}
        </li>
      );
    });

  const renderSeasonOffers = () =>
    data.OfferSeason.map((o) => {
      const colour = colourOf(o.Style);
      const d = parse(o.CountdownTimeDate);
      const left = d ? countdown(d) : null;
      return (
        <li key={o.Id} className="rmapo-card rmapo-card--offer" style={colour ? { '--rmapo-banner': colour } : undefined}>
          <div className="rmapo-banner">
            <strong>{o.SalePercent}</strong>
            {renderLive(isOn(o.Approved))}
          </div>
          <h3>{o.Title}</h3>
          {d && (
            <p className={`rmapo-countdown ${left.ended ? 'is-ended' : ''}`}>
              <FontAwesomeIcon icon={faHourglassHalf} /> {left.text}
              <span>until {d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
            </p>
          )}
          {left?.ended && isOn(o.Approved) && <p className="rmapo-warn">The countdown has ended but the offer is still live.</p>}
          {renderActions(o)}
        </li>
      );
    });

  // ---------- Form fields (same fields per tab as before) ----------
  const field = (label, name, props = {}) => (
    <label className="rmapo-field" key={name}>
      <span>{label}</span>
      <input name={name} value={formData[name] ?? ''} onChange={handleInputChange} {...props} />
    </label>
  );

  const approvedSwitch = (
    <label className={`rmapo-switch ${isOn(formData.Approved) ? 'is-on' : ''}`}>
      <input type="checkbox" name="Approved" checked={isOn(formData.Approved)} onChange={handleInputChange} />
      <i aria-hidden="true" />
      <span>
        <strong>Show on the website</strong>
        <small>Approved</small>
      </span>
    </label>
  );

  const styleField = (placeholder) => {
    const colour = colourOf(formData.Style);
    return (
      <label className="rmapo-field">
        <span>{tab.key === 'packages' ? 'Text colour style' : 'Background style'}</span>
        <div className="rmapo-style">
          <i className="rmapo-swatch" style={colour ? { background: colour } : undefined} aria-hidden="true" />
          <input type="text" name="Style" value={formData.Style ?? ''} onChange={handleInputChange} placeholder={placeholder} />
        </div>
      </label>
    );
  };

  const renderFormFields = () => {
    if (tab.key === 'packages') {
      return (
        <>
          {field('Package name *', 'PackageName', { type: 'text', required: true, autoFocus: true })}
          <div className="rmapo-three">
            {field('Price ($) *', 'Price', { type: 'number', required: true, min: '0', step: '0.01', inputMode: 'decimal' })}
            {field('Offer price ($)', 'OfferPrice', { type: 'number', min: '0', step: '0.01', inputMode: 'decimal' })}
            {field('Session price ($) *', 'SessionPrice', { type: 'number', required: true, min: '0', step: '0.01', inputMode: 'decimal' })}
          </div>
          <div className="rmapo-three">
            {field('Sessions *', 'NbOfSessions', { type: 'number', required: true, min: '0' })}
            {field('Levels *', 'NumberOfLevels', { type: 'number', required: true, min: '0' })}
            {field('Sessions / week *', 'SessionsPerWeek', { type: 'number', required: true, min: '0' })}
          </div>
          {styleField('color: rgb(245, 152, 49);')}
          {field('Button style class', 'StyleButton', { type: 'text', placeholder: 'it-price-title-bg-1' })}
        </>
      );
    }
    if (tab.key === 'sectionOffers') {
      return (
        <>
          {field('Title *', 'Title', { type: 'text', required: true, autoFocus: true })}
          <label className="rmapo-field">
            <span>Details * (HTML allowed)</span>
            <textarea name="Details" rows={5} value={formData.Details ?? ''} onChange={handleInputChange} required />
          </label>
          {styleField('background-color: #a2cdee;')}
          <div className="rmapo-field">
            <span>Image</span>
            <label className="rmapo-file-pick">
              <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && setImageFile(e.target.files[0])} />
              <FontAwesomeIcon icon={faUpload} />
              <span>
                <strong>{imageFile ? imageFile.name : formData.Img || 'Choose an image'}</strong>
                <small>{imageFile ? 'New image — saved when you press Save' : formData.Img ? 'Current image · tap to replace' : 'JPG or PNG'}</small>
              </span>
            </label>
          </div>
          {approvedSwitch}
        </>
      );
    }
    return (
      <>
        {field('Title *', 'Title', { type: 'text', required: true, autoFocus: true })}
        <div className="rmapo-two">
          {field('Sale text *', 'SalePercent', { type: 'text', required: true, placeholder: '20% Off! 🎁' })}
          {field('Countdown ends *', 'CountdownTimeDate', { type: 'datetime-local', required: true })}
        </div>
        {styleField('background-color: #a2cdee;')}
        {approvedSwitch}
      </>
    );
  };

  // ---------- Page ----------
  const list = data[tab.table] || [];

  return (
    <div className="rmapo">
      <header className="rmapo-header">
        <div>
          <h1>Prices & offers</h1>
          <p>Packages and offers shown on the website.</p>
        </div>
        <button type="button" className="rmapo-btn" onClick={handleAddNew} disabled={loading || Boolean(loadError)}>
          <FontAwesomeIcon icon={faPlus} /> Add {tab.one}
        </button>
      </header>

      <div className="rmapo-tabs" role="tablist" aria-label="Prices and offers">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab.key === t.key} className={tab.key === t.key ? 'is-on' : ''} onClick={() => setTab(t.key)}>
            <FontAwesomeIcon icon={t.icon} /> {t.label} {!loading && <span>{(data[t.table] || []).length}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rmapo-grid" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rmapo-skel" />
          ))}
        </div>
      ) : loadError ? (
        <div className="rmapo-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{loadError}</p>
          <button type="button" className="rmapo-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      ) : list.length === 0 ? (
        <div className="rmapo-empty">
          <FontAwesomeIcon icon={tab.icon} />
          <p>No {tab.label.toLowerCase()} yet.</p>
        </div>
      ) : (
        <ul className="rmapo-grid">
          {tab.key === 'packages' ? renderPackages() : tab.key === 'sectionOffers' ? renderSectionOffers() : renderSeasonOffers()}
        </ul>
      )}

      {/* ---------- Add / edit ---------- */}
      {showModal && (
        <div className="rmapo-overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}>
          <form className="rmapo-modal" role="dialog" aria-modal="true" aria-labelledby="rmapo-form-title" onSubmit={handleSubmit}>
            <div className="rmapo-modal-head">
              <h2 id="rmapo-form-title">
                {editingItem ? 'Edit' : 'Add'} {tab.one}
              </h2>
              <button type="button" className="rmapo-x" onClick={() => setShowModal(false)} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="rmapo-modal-body">
              {renderFormFields()}
              {formError && (
                <p className="rmapo-error" role="alert">
                  {formError}
                </p>
              )}
            </div>
            <div className="rmapo-modal-foot">
              <button type="button" className="rmapo-btn rmapo-btn--ghost" onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="rmapo-btn" disabled={saving}>
                {saving ? 'Saving…' : editingItem ? 'Save changes' : `Add ${tab.one}`}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------- Delete confirm ---------- */}
      {toDelete && (
        <div className="rmapo-overlay" onMouseDown={(e) => e.target === e.currentTarget && !deleteBusy && setToDelete(null)}>
          <div className="rmapo-dialog" role="alertdialog" aria-modal="true" aria-labelledby="rmapo-del-title">
            <span className="rmapo-dialog-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2 id="rmapo-del-title">Delete this {tab.one}?</h2>
            <p>
              <strong>{nameOf(toDelete)}</strong>
              <br />
              It will disappear from the website. This can’t be undone.
            </p>
            {deleteError && (
              <p className="rmapo-error" role="alert">
                {deleteError}
              </p>
            )}
            <div className="rmapo-dialog-actions">
              <button type="button" className="rmapo-btn rmapo-btn--ghost" onClick={() => setToDelete(null)} disabled={deleteBusy}>
                Cancel
              </button>
              <button type="button" className="rmapo-btn rmapo-btn--danger" onClick={handleDelete} disabled={deleteBusy}>
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmapo-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default PricesAndOffers;